// WhatsApp template + deeplink builder untuk activation flow.
// Dipakai di AdminView (Pending Premium tab) bila admin tap "Aktifkan + Wasap".

import { listPremiumPlanModules } from '../config/modules';

const ADMIN_WHATSAPP = '601135011231';

// Derive wasap handle dari email prefix.
// Contoh: 'farahfarzana3398@gmail.com' → 'farahfarzana3398'
export function getWasapHandle(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

// Format tarikh BM (contoh: "8 Jun 2026").
function formatBmDate(input) {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Senarai modul untuk template (sama style macam pricing card — exclude smartcustom).
function buildModuleList() {
  return listPremiumPlanModules()
    .filter(m => m.moduleId !== 'smartcustom')
    .map(m => m.marketingName)
    .join(' + ');
}

// Build activation message body (sama persis macam template manual user).
// isRenewal: true → message bahawa RM99 renewal. false/undefined → RM199 new.
export function buildActivationMessage({ email, licenseKey, expiresAt, durationDays = 30, isRenewal = false }) {
  const handle = getWasapHandle(email);
  const tarikhTamat = formatBmDate(expiresAt);
  const modules = buildModuleList();

  const greetingLine = isRenewal
    ? 'Terima kasih atas RENEWAL RM99 untuk Pakej Premium SESAAT 🎉'
    : 'Terima kasih atas pembayaran RM199 untuk Pakej Premium SESAAT 🎉';

  const statusLine = isRenewal
    ? '✅ Akaun awak dah RENEW & AKTIF balik — terus login dan sambung guna:'
    : '✅ Akaun awak dah AKTIF — tak perlu buat apa-apa lagi. Terus login dan guna je:';

  return [
    `*Assalamualaikum & hai ${handle}* 👋`,
    '',
    greetingLine,
    '',
    statusLine,
    '',
    '🔗 https://app.sesaat.ai/login',
    '',
    '📝 Butiran langganan:',
    `• Email: ${email}`,
    `• Tempoh: ${durationDays} hari (sampai ${tarikhTamat}).`,
    `• Modul: ${modules}.`,
    '• 3 slot profile browser & 3 slot avatar.',
    '',
    '⚠️ Reminder penting:',
    'Untuk jana gambar & video AI, awak perlukan akaun Flow (Google Labs) Ultra (berasingan daripada SESAAT). Kalau belum ada, reply chat ni — kami boleh bantu langgan. Ada pilihan akaun *share* (murah) atau *private*.',
    '',
    'Ada apa-apa masalah, tak faham, atau nak tanya — reply chat ni je. Kami standby bantu.',
    '',
    'Video Tutorial: https://www.youtube.com/playlist?list=PLUgKht1i8s1Yo9oEW_L4yUE4o_NWx1oIX',
    'Group support: https://chat.whatsapp.com/GJtccD9aKu12gWYgFtxsGr',
    '',
    isRenewal ? 'Selamat sambung meroket dengan SESAAT! 🚀' : 'Selamat meroket dengan SESAAT! 🚀'
  ].join('\n');
}

// Normalize nombor wasap MY ke format wa.me (digits only, with country code).
// Input boleh '+60113...', '0113...', '60113...' — output '60113...'
export function normalizeWaNumber(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('60')) return digits;
  if (digits.startsWith('0')) return '60' + digits.slice(1);
  return digits;
}

// Build wa.me deeplink utk activation message (admin tap → buka WhatsApp customer).
export function buildActivationDeeplink({ customerWhatsapp, email, licenseKey, expiresAt, durationDays = 30, isRenewal = false }) {
  const num = normalizeWaNumber(customerWhatsapp);
  const msg = buildActivationMessage({ email, licenseKey, expiresAt, durationDays, isRenewal });
  if (!num) return null; // takda nombor wasap — caller kena handle fallback
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
}

// Build wa.me deeplink utk user chase admin (selepas hantar submission).
// User tap → buka WhatsApp dengan admin nombor + ayat pre-fill.
export function buildChaseAdminDeeplink({ email, amountSen, flowAddon = false } = {}) {
  const amountRM = Number.isFinite(amountSen) ? amountSen / 100 : 199; // fallback RM199
  const produk = flowAddon
    ? 'Pakej Premium SESAAT + Flow Ultra (Sharing)'
    : 'Pakej Premium SESAAT';
  const msg = `Assalamualaikum, saya pengguna (${email || ''}) dah transfer RM${amountRM} untuk ${produk}. Terima kasih!`;
  return `https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(msg)}`;
}

// Build renewal reminder message body — ADMIN hantar ke USER.
// Dipakai dalam admin dashboard "Expiring Soon" → admin tap → buka wasap user.
export function buildRenewalReminderMessage({ email, expiresAt, daysLeft }) {
  const handle = getWasapHandle(email);
  const tarikhTamat = formatBmDate(expiresAt);
  const isFinal = daysLeft <= 1;

  return [
    `*Assalamualaikum & hai ${handle}* 👋`,
    '',
    isFinal
      ? '⚠️ *REMINDER URGENT* — Lesen SESAAT awak akan tamat ESOK!'
      : `🔔 *REMINDER* — Lesen SESAAT awak akan tamat dalam *${daysLeft} hari*.`,
    '',
    `📅 Tarikh tamat: *${tarikhTamat}*`,
    '',
    '🔄 *Renew RM99 sahaja* untuk extend +30 hari:',
    '',
    '1. Klik 👉 https://app.sesaat.ai/pricing',
    '2. Transfer RM99 ke akaun bank',
    '3. Upload bukti payment',
    '4. Akaun aktif balik dalam 1-24 jam',
    '',
    '*Modul akses + setting sedia ada akan kekal sama.*',
    '',
    'Ada soalan, reply chat ni je. Terima kasih! 🙏'
  ].join('\n');
}

// Build wa.me deeplink untuk admin hantar reminder ke user.
// Caller pass userWhatsapp (dari users.whatsappNumber), email, expiresAt, daysLeft.
// Return null kalau takda nombor wasap user → caller fallback ke email reminder.
export function buildRenewalReminderDeeplink({ userWhatsapp, email, expiresAt, daysLeft }) {
  const num = normalizeWaNumber(userWhatsapp);
  if (!num) return null;
  const msg = buildRenewalReminderMessage({ email, expiresAt, daysLeft });
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
}
// ═══════════════════════════════════════════════════════════════
// FLOW ULTRA — mesej admin → penyewa
// ═══════════════════════════════════════════════════════════════
// Nada sengaja SAMA dengan buildActivationMessage/buildRenewalReminderMessage
// di atas ("awak", emoji, *bold*, langkah bernombor) — pelanggan yang sama
// menerima kedua-duanya, jadi dua nada berbeza akan berbunyi seperti dua
// syarikat. Teks diluluskan oleh pemilik 26 Ogos 2026.
//
// ⛔ KATA LALUAN TIDAK PERNAH MASUK KE DALAM MESEJ INI.
//    Teks prefill masuk ke dalam alamat wa.me, dan alamat itu tersimpan dalam
//    sejarah browser admin. Kata laluan yang dikongsi empat pelanggan berbayar
//    tidak boleh duduk di situ. Mesej "access" sengaja berkata butiran login
//    menyusul — admin menampalnya sebagai mesej KEDUA daripada butang Copy.
//    Jangan tambah medan kata laluan pada fungsi ini.

const FLOW_PAY_URL = 'https://app.sesaat.ai/flow';

// Nama untuk sapaan. Guna nama sebenar kalau ada; kalau tidak, jatuh ke bahagian
// depan emel — corak sama getWasapHandle, kerana majoriti pembeli Flow tidak
// pernah ditanya nama semasa membayar.
function flowGreetName(name, email) {
  const n = (name || '').trim();
  if (n && n !== email) return n;
  return getWasapHandle(email) || 'awak';
}

// stage: 'access' | 'remind' | 'final' | 'expired'
export function buildFlowMessage({
  stage, name, email, expiresAt, daysLeft = 0,
  waitedDays = 0, extendedDays = 0, graceDays = 3,
}) {
  const hai = `*Assalamualaikum & hai ${flowGreetName(name, email)}* 👋`;
  const tarikh = formatBmDate(expiresAt);

  if (stage === 'access') {
    const baris = [
      hai,
      '',
      'Terima kasih atas pembayaran RM100 untuk *Akaun Flow Ultra (Sharing)* 🎉',
    ];

    /* Ayat maaf hanya muncul bila penantian memang lama. Sistem yang berjalan
       betul menyerahkan akaun pada hari yang sama, jadi 2 hari pun sudah lewat.
       Di bawah itu, mengungkitnya hanya menimbulkan persoalan yang tiada. */
    if (waitedDays >= 2) {
      baris.push('');
      if (extendedDays > 0) {
        baris.push(
          `Minta maaf atas kelewatan — awak dah tunggu *${waitedDays} hari* sejak bayar, dan itu memang terlalu lama.`,
          '',
          `Sebab tu saya dah *tambah ${extendedDays} hari* pada langganan awak. Awak tetap dapat 30 hari penuh. Tarikh tamat baru: *${tarikh}*`,
        );
      } else {
        baris.push(
          `Minta maaf atas kelewatan — awak dah tunggu *${waitedDays} hari* sejak bayar, dan itu memang terlalu lama. Terima kasih sebab sabar tunggu 🙏`,
        );
      }
    }

    baris.push(
      '',
      '✅ Akaun awak dah siap. Butiran login saya hantar dalam mesej seterusnya ya.',
      '',
      '📝 Butiran langganan:',
      `• Tempoh: 30 hari (sampai *${tarikh}*)`,
      '• Jenis: Akaun kongsi — 4 orang satu akaun',
      '',
      '⚠️ Penting, sila baca:',
      '• *Jangan tukar password* akaun tu. Ada 3 orang lain guna akaun sama — kalau ditukar, semua orang terputus.',
      '• *Jangan logout* peranti orang lain.',
      '• Guna untuk jana gambar & video je, jangan ubah setting akaun.',
      '',
      'Ada masalah login, reply chat ni je. Kami standby bantu 🙏',
    );
    return baris.join('\n');
  }

  if (stage === 'final') {
    return [
      hai,
      '',
      `⚠️ *REMINDER URGENT* — Akaun Flow Ultra awak tamat *${daysLeft <= 0 ? 'HARI NI' : 'ESOK'}* (${tarikh}).`,
      '',
      `Sambung sekarang RM100 👉 ${FLOW_PAY_URL}`,
      '',
      'Bayar online, sah serta-merta, login kekal sama.',
      '',
      `Lepas tamat, slot dilepaskan dalam ${graceDays} hari dan password ditukar. Selepas tu kena daftar semula macam baru.`,
      '',
      'Reply chat ni kalau perlu bantuan 🙏',
    ].join('\n');
  }

  if (stage === 'expired') {
    const lepas = Math.abs(daysLeft);
    return [
      hai,
      '',
      `Akaun Flow Ultra awak dah tamat *${tarikh}* — ${lepas} hari lepas.`,
      '',
      'Slot awak masih *belum* dilepaskan lagi. Kalau nak sambung, boleh buat sekarang:',
      '',
      `👉 ${FLOW_PAY_URL} — RM100 untuk 30 hari`,
      '',
      'Kalau tak sambung, slot akan diberi kepada orang lain dan password ditukar.',
      '',
      'Reply chat ni kalau ada soalan 🙏',
    ].join('\n');
  }

  // 'remind' — masih ada masa
  return [
    hai,
    '',
    `🔔 *REMINDER* — Akaun Flow Ultra awak akan tamat dalam *${daysLeft} hari*.`,
    '',
    `📅 Tarikh tamat: *${tarikh}*`,
    '',
    '🔄 *Sambung RM100* untuk extend +30 hari:',
    '',
    `1. Klik 👉 ${FLOW_PAY_URL}`,
    '2. Bayar online (kad / FPX) — sah serta-merta',
    '3. Akaun terus sambung, *login sama, tak perlu tukar apa-apa*',
    '',
    '⚠️ Kalau tak sambung, slot awak akan dilepaskan dan password akaun ditukar.',
    '',
    'Ada soalan, reply chat ni je. Terima kasih! 🙏',
  ].join('\n');
}

// Pulangan null bila tiada nombor — pemanggil WAJIB menyembunyikan butang,
// bukan membuka wa.me kosong (itu membuka WhatsApp tanpa penerima dan admin
// menyangka mesej sudah dihantar).
export function buildFlowDeeplink(opts) {
  const num = normalizeWaNumber(opts.phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(buildFlowMessage(opts))}`;
}
